import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import BackofficeReportsApi from './components/biReports/backofficeReportsApi';
import ClientMetricCard from './components/biReports/ClientMetricCard';
import ClientsAccountValidation from './components/biReports/ClientsAccountValidation';
import ClientsActivationBreakdown from './components/biReports/ClientsActivationBreakdown';
import ClientsActivationChart from './components/biReports/ClientsActivationChart';

const numberFormatter = new Intl.NumberFormat('ro-RO', {
  maximumFractionDigits: 0,
});

function formatPeriodRange(period) {
  if (!period?.dateFrom || !period?.dateTo) return '';
  const options = { day: '2-digit', month: 'short', year: 'numeric' };
  const from = new Date(`${period.dateFrom}T12:00:00`).toLocaleDateString('ro-RO', options);
  const to = new Date(`${period.dateTo}T12:00:00`).toLocaleDateString('ro-RO', options);
  return `${from} – ${to}`;
}

function formatUpdateTime(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleTimeString('ro-RO', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function metricValue(metric) {
  if (!metric || metric.rate === null || typeof metric.rate === 'undefined') {
    return '—';
  }
  return `${Number(metric.rate).toFixed(1)}%`;
}

function metricDetail(metric) {
  if (!metric) return '';
  if (metric.rate === null || typeof metric.rate === 'undefined') {
    return 'Not enough mature clients';
  }
  const pending = Number(metric.pending_maturity || 0);
  return `${numberFormatter.format(Number(metric.converted || 0))} of ${numberFormatter.format(Number(metric.eligible || 0))} eligible${
    pending ? ` · ${numberFormatter.format(pending)} pending` : ''
  }`;
}

function csvCell(value) {
  const clean = value === null || typeof value === 'undefined' ? '' : String(value);
  return `"${clean.replace(/"/g, '""')}"`;
}

export function accountValidationCsvRows(summary = {}) {
  const validation = summary.account_validation || {};
  return [
    ['Account validation', 'Clients', 'Share of registrations %'].map(csvCell).join(','),
    ['Validated', validation.validated?.value, validation.validated?.share].map(csvCell).join(','),
    ['Not validated', validation.not_validated?.value, validation.not_validated?.share].map(csvCell).join(','),
    ['Suspended', validation.suspended?.value, validation.suspended?.share].map(csvCell).join(','),
  ];
}

class RapClientsActivation extends React.Component {
  constructor(props) {
    super(props);
    this.isMountedFlag = false;
    this.initialLoadTimer = null;
    this.request = null;
    this.requestId = 0;
    this.cache = {};
    this.cacheUpdatedAt = {};
    this.state = {
      periodMode: 'last_90d',
      countyFilter: 'all',
      windowDays: 7,
      breakdownDimension: 'county',
      countyOptions: [{ value: 'all', label: 'All counties' }],
      response: null,
      status: 'idle',
      updatedAt: null,
    };
  }

  componentDidMount() {
    this.isMountedFlag = true;
    this.initialLoadTimer = window.setTimeout(() => {
      this.initialLoadTimer = null;
      if (this.isMountedFlag) this.loadActivation();
    }, 0);
  }

  componentWillUnmount() {
    this.isMountedFlag = false;
    if (this.initialLoadTimer !== null) {
      window.clearTimeout(this.initialLoadTimer);
      this.initialLoadTimer = null;
    }
    this.requestId += 1;
    if (this.request && typeof this.request.abort === 'function') {
      this.request.abort();
    }
    this.request = null;
  }

  cacheKey = (
    periodMode = this.state.periodMode,
    county = this.state.countyFilter,
    windowDays = this.state.windowDays,
  ) => `${periodMode}:${county}:${windowDays}`;

  loadActivation = ({ force = false } = {}) => {
    const { periodMode, countyFilter, windowDays } = this.state;
    const cacheKey = this.cacheKey(periodMode, countyFilter, windowDays);
    if (!force && this.cache[cacheKey]) {
      this.requestId += 1;
      if (this.request && typeof this.request.abort === 'function') {
        this.request.abort();
      }
      this.request = null;
      this.setState({
        response: this.cache[cacheKey],
        status: 'success',
        updatedAt: this.cacheUpdatedAt[cacheKey] || null,
      });
      return;
    }

    const requestId = this.requestId + 1;
    this.requestId = requestId;
    if (this.request && typeof this.request.abort === 'function') {
      this.request.abort();
    }

    const request = BackofficeReportsApi.getClientsActivationAsync(
      this.props.client,
      {
        periodMode,
        windowDays,
        county: countyFilter,
        granularity: 'auto',
      },
    );
    this.request = request;
    this.setState({ status: 'loading' });

    const handleSuccess = (data) => {
      if (!this.isMountedFlag || this.requestId !== requestId) return;
      this.request = null;
      const updatedAt = data?.generated_at || new Date().toISOString();
      this.cache[cacheKey] = data;
      this.cacheUpdatedAt[cacheKey] = updatedAt;
      this.setState({
        response: data || null,
        countyOptions: Array.isArray(data?.county_options) && data.county_options.length
          ? data.county_options
          : this.state.countyOptions,
        status: 'success',
        updatedAt,
      });
    };
    const handleFailure = (xhr, textStatus) => {
      if (textStatus === 'abort') return;
      if (!this.isMountedFlag || this.requestId !== requestId) return;
      this.request = null;
      this.setState({ status: 'error' });
    };

    if (request && typeof request.done === 'function') {
      request.done(handleSuccess);
      request.fail(handleFailure);
    } else {
      Promise.resolve(request).then(handleSuccess).catch(handleFailure);
    }
  };

  selectCachedResponse = (periodMode, countyFilter, windowDays) => ({
    response: this.cache[this.cacheKey(periodMode, countyFilter, windowDays)] || null,
    updatedAt: this.cacheUpdatedAt[
      this.cacheKey(periodMode, countyFilter, windowDays)
    ] || null,
  });

  handlePeriodChange = (event, periodMode) => {
    if (!periodMode || periodMode === this.state.periodMode) return;
    const cached = this.selectCachedResponse(
      periodMode,
      this.state.countyFilter,
      this.state.windowDays,
    );
    this.setState({ periodMode, ...cached }, () => this.loadActivation());
  };

  handleCountyChange = (event, option) => {
    const countyFilter = option?.value || 'all';
    if (countyFilter === this.state.countyFilter) return;
    const cached = this.selectCachedResponse(
      this.state.periodMode,
      countyFilter,
      this.state.windowDays,
    );
    this.setState({ countyFilter, ...cached }, () => this.loadActivation());
  };

  handleWindowChange = (event, windowDays) => {
    if (!windowDays || windowDays === this.state.windowDays) return;
    const cached = this.selectCachedResponse(
      this.state.periodMode,
      this.state.countyFilter,
      windowDays,
    );
    this.setState({ windowDays, ...cached }, () => this.loadActivation());
  };

  handleDimensionChange = (event, breakdownDimension) => {
    if (breakdownDimension) this.setState({ breakdownDimension });
  };

  exportCsv = () => {
    const { response, breakdownDimension } = this.state;
    if (!response) return;
    const period = response.period || {};
    const filters = response.filters || {};
    const county = filters.county || {};
    const summary = response.summary || {};
    const curve = response.activation_curve || {};
    const breakdown = response.breakdowns?.[breakdownDimension] || [];
    const dataQuality = response.data_quality || {};
    const lines = [
      ['Client activation'].map(csvCell).join(','),
      ['Registration cohort', period.label].map(csvCell).join(','),
      ['Date from', period.dateFrom].map(csvCell).join(','),
      ['Date to', period.dateTo].map(csvCell).join(','),
      ['Observation time', response.as_of].map(csvCell).join(','),
      ['Timezone', period.timezone].map(csvCell).join(','),
      ['Client profile county', county.label].map(csvCell).join(','),
      ['Selected activation window', `${filters.windowDays} days`].map(csvCell).join(','),
      ['Window definition', 'Exact elapsed 24-hour days from registration to first qualifying purchase'].map(csvCell).join(','),
      ['Qualifying order status codes', '1, 2, 5'].map(csvCell).join(','),
      ['Definitions version', response.definitions_version].map(csvCell).join(','),
      '',
      ...accountValidationCsvRows(summary),
      '',
      ['Metric', 'Registrations', 'Eligible', 'Converted', 'Pending', 'Rate %', 'Median hours'].map(csvCell).join(','),
      ['Registrations', summary.registrations, '', '', '', '', ''].map(csvCell).join(','),
      ...[1, 7, 30].map((days) => {
        const metric = summary[`conversion_${days}d`] || {};
        return [
          `Converted within ${days}d`,
          summary.registrations,
          metric.eligible,
          metric.converted,
          metric.pending_maturity,
          metric.rate,
          metric.median_hours,
        ].map(csvCell).join(',');
      }),
      '',
      ['Activation curve', 'Elapsed days', 'Eligible 30d', 'Converted', 'Rate %'].map(csvCell).join(','),
      ...(curve.rows || []).map((row) => [
        'Curve', row.elapsedDays, curve.eligible_30d, row.converted, row.rate,
      ].map(csvCell).join(',')),
      '',
      ['Cohort trend', 'Date from', 'Date to', 'Registrations', 'Eligible', 'Converted', 'Pending', 'Rate %', 'Complete'].map(csvCell).join(','),
      ...(response.trend || []).map((row) => [
        'Trend', row.dateFrom, row.dateTo, row.registrations, row.eligible,
        row.converted, row.pending_maturity, row.rate, row.complete,
      ].map(csvCell).join(',')),
      '',
      [`Breakdown: ${breakdownDimension}`, 'Segment', 'Registrations', 'Eligible', 'Converted', 'Pending', 'Rate %', 'Median hours'].map(csvCell).join(','),
      ...breakdown.map((row) => [
        breakdownDimension, row.label, row.registrations, row.eligible,
        row.converted, row.pending_maturity, row.rate, row.median_hours,
      ].map(csvCell).join(',')),
      '',
      ['Data quality', 'Count'].map(csvCell).join(','),
      ['Purchase before registration', dataQuality.purchase_before_registration].map(csvCell).join(','),
      ['Future first purchase', dataQuality.future_first_purchase].map(csvCell).join(','),
      ['Future registration', dataQuality.future_registration].map(csvCell).join(','),
      ['Excluded from activation', dataQuality.excluded_from_activation].map(csvCell).join(','),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `client_activation_${period.periodMode || 'period'}_${period.dateFrom || 'from'}_${period.dateTo || 'to'}_${county.value || 'all'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  render() {
    const {
      periodMode,
      countyFilter,
      windowDays,
      breakdownDimension,
      countyOptions,
      response,
      status,
      updatedAt,
    } = this.state;
    const summary = response?.summary || {};
    const selectedCounty = countyOptions.find(
      (option) => option.value === countyFilter,
    ) || { value: countyFilter, label: response?.filters?.county?.label || countyFilter };
    const loading = status === 'loading';
    const hasResponse = Boolean(response);
    const excluded = Number(response?.data_quality?.excluded_from_activation || 0);

    return (
      <Stack spacing={{ xs: 1, sm: 1.5 }}>
        <Stack
          spacing={1.1}
          sx={{
            p: { xs: 1.25, sm: 1.75, md: 2 },
            bgcolor: '#fff',
            borderRadius: { xs: '14px', sm: '16px' },
            border: '1px solid rgba(15, 23, 42, 0.06)',
            boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Box minWidth={0}>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.25rem' }, color: '#020617' }}>
                Client activation
              </Typography>
              <Typography sx={{ mt: 0.2, color: '#475569', fontSize: { xs: '0.72rem', sm: '0.82rem' } }}>
                Registration to first purchase
              </Typography>
            </Box>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={periodMode}
              onChange={this.handlePeriodChange}
              aria-label="Activation registration cohort"
              sx={{
                flexShrink: 0,
                '& .MuiToggleButton-root': {
                  minWidth: { xs: 38, sm: 48 },
                  px: { xs: 0.65, sm: 1.1 },
                  py: 0.4,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: { xs: '0.65rem', sm: '0.74rem' },
                },
              }}
            >
              <ToggleButton value="last_30d">30d</ToggleButton>
              <ToggleButton value="last_90d">90d</ToggleButton>
              <ToggleButton value="last_180d">180d</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={0.75}
          >
            <Box>
              {hasResponse && (
                <Typography sx={{ color: '#64748b', fontSize: '0.74rem', fontWeight: 650 }}>
                  {formatPeriodRange(response.period)} · {selectedCounty.label}
                </Typography>
              )}
              {updatedAt && (
                <Typography sx={{ color: '#64748b', fontSize: '0.68rem', mt: 0.1 }}>
                  Updated {formatUpdateTime(updatedAt)}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={0.75}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadRoundedIcon />}
                onClick={this.exportCsv}
                disabled={!hasResponse}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
              >
                CSV
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshRoundedIcon />}
                onClick={() => this.loadActivation({ force: true })}
                disabled={loading}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            spacing={1}
          >
            <Stack direction="row" alignItems="center" spacing={0.75} minWidth={0}>
              <LocationOnRoundedIcon sx={{ color: '#1d4ed8', fontSize: 18 }} />
              <Box minWidth={0}>
                <Typography sx={{ color: '#0f172a', fontWeight: 800, fontSize: '0.76rem' }}>
                  Client profile county
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: '0.68rem' }}>
                  Filters activation by current address county.
                </Typography>
              </Box>
            </Stack>
            <Autocomplete
              size="small"
              disableClearable
              autoHighlight
              options={countyOptions}
              value={selectedCounty}
              onChange={this.handleCountyChange}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search or select county"
                  inputProps={{ ...params.inputProps, 'aria-label': 'Activation client profile county' }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnRoundedIcon sx={{ color: '#64748b', fontSize: 17 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              sx={{
                width: { xs: '100%', md: 290 },
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                  borderRadius: '10px',
                  fontSize: { xs: '16px', sm: '0.8rem' },
                },
                '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                  fontSize: { xs: '16px', sm: '0.8rem' },
                },
              }}
            />
          </Stack>
        </Stack>

        {status === 'error' && (
          <Alert
            severity="error"
            action={(
              <Button color="inherit" size="small" onClick={() => this.loadActivation({ force: true })}>
                Retry
              </Button>
            )}
          >
            Activation data could not be loaded.
          </Alert>
        )}

        <Box
          component="section"
          aria-label="Activation summary metrics"
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            gap: { xs: 0.75, sm: 1, md: 1.5 },
            alignItems: 'stretch',
            '& > *': { minWidth: 0 },
          }}
        >
          <Box sx={{ minWidth: 0, height: '100%' }}>
            <ClientMetricCard
              title="Registrations"
              value={hasResponse ? summary.registrations : undefined}
              detail="Selected registration cohort"
              loading={loading}
              accent="#1d4ed8"
            />
          </Box>
          {[1, 7, 30].map((days) => {
            const metric = summary[`conversion_${days}d`];
            return (
              <Box sx={{ minWidth: 0, height: '100%' }} key={days}>
                <ClientMetricCard
                  title={`Within ${days} day${days === 1 ? '' : 's'}`}
                  value={hasResponse ? metricValue(metric) : undefined}
                  detail={metricDetail(metric)}
                  definition={`First purchase within ${days * 24} hours; only clients with the full window are eligible.`}
                  loading={loading}
                  accent={days === 1 ? '#0284c7' : days === 7 ? '#00897b' : '#7c3aed'}
                />
              </Box>
            );
          })}
        </Box>

        <ClientsAccountValidation
          validation={summary.account_validation}
          status={status}
        />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={0.8}
          sx={{
            p: { xs: 1, sm: 1.2 },
            bgcolor: '#eff6ff',
            border: '1px solid #dbeafe',
            borderRadius: '12px',
          }}
        >
          <Typography sx={{ color: '#334155', fontSize: '0.76rem' }}>
            Rates include only clients who had the full time window to purchase.
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={windowDays}
            onChange={this.handleWindowChange}
            aria-label="Activation analysis window"
            sx={{
              '& .MuiToggleButton-root': {
                flex: { xs: 1, sm: 'initial' },
                minWidth: 48,
                py: 0.35,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.72rem',
              },
            }}
          >
            <ToggleButton value={1}>1d</ToggleButton>
            <ToggleButton value={7}>7d</ToggleButton>
            <ToggleButton value={30}>30d</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <ClientsActivationChart response={response} status={status} />

        <ClientsActivationBreakdown
          response={response}
          status={status}
          dimension={breakdownDimension}
          onDimensionChange={this.handleDimensionChange}
        />

        {excluded > 0 && (
          <Alert severity="warning">
            {numberFormatter.format(excluded)} client record{excluded === 1 ? '' : 's'} excluded because purchase dates precede registration or are in the future.
          </Alert>
        )}
      </Stack>
    );
  }
}

export default RapClientsActivation;
